import './consentsByRegion.less'
import { EmptyConsentsByRegionWidget } from './emptyWidgets'
import { CustomTooltipCR } from './cookieConsentCustomTooltip'
import WidgetCard from '../../../../components/WidgetCard'
import { dateMaps, getTimeDropdownOptions } from '../constants'
import {
  ACTION_GET_COOKIE_CONSENTS_BY_REGION,
  ConsentsByCategory,
  ConsentsByRegion,
  CookieConsentDistributionParams,
  fetchCookieConsentByCategory,
  fetchCookieConsentsByRegion
} from '../../cookieConsentSlice'
import { RootState } from '../../../../rootReducer'
import historyService from '../../../../services/historyService'
import { URL_COOKIES_CONSENT, URL_DOMAINS } from '../../../../constants'
import ChartBarLegends from '../../../consentManagement/components/chartBarLegends/chartBarLegends'
import { ChartColors } from '../../../dashboards/mainDashboardv2/constants'
import ChartBarSimple from '../../../../components/ChartBarSimple/chartBarSimple'
import {
  CookieConsentDomain,
  CookieConsentDomainsParams,
  CookieConsentLogsVersionList,
  CookieConsentS3Params,
  CookieConsentSortBy,
  CookieConsentSortOrder,
  fetchCookieConsentDomains,
  fetchCookieConsentVersions
} from '../../domains/domainsSlice'
import { getShowLoader } from '../../../../reducers/requestReducer'
import LoaderView from '../../../../components/Loader/loader'
import { getCCVersionFromName, getNewCookiesCount } from '../../domains/domainUtils'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { Tags, Typography } from '@lightbeamai/design-system'

interface IProps {
  fetchCookieConsentsByRegion: (params: CookieConsentDistributionParams) => void
  fetchCookieConsentDomains: (params: CookieConsentDomainsParams) => void
  fetchCookieConsentVersions: (params: Partial<CookieConsentS3Params>) => void
  fetchCookieConsentByCategory: (params: CookieConsentDistributionParams) => void
  consentsByRegion?: ConsentsByRegion
  domains: CookieConsentDomain[]
  versionsList?: CookieConsentLogsVersionList
  consentsByCategory?: ConsentsByCategory
  showLoader: boolean
  showSummary?: boolean
  showDurationDropdown?: boolean
  widgetTitle?: string
  timePeriod?: string
}

const ConsentsByRegionWidget = (props: IProps) => {
  const {
    consentsByRegion = {},
    fetchCookieConsentsByRegion,
    fetchCookieConsentDomains,
    fetchCookieConsentVersions,
    fetchCookieConsentByCategory,
    domains = [],
    versionsList = [],
    consentsByCategory = {},
    showLoader,
    showSummary = false,
    showDurationDropdown = true,
    widgetTitle,
    timePeriod = ''
  } = props
  const intl = useIntl()

  const durationOptions = getTimeDropdownOptions(intl)
  const [duration, setDuration] = useState(durationOptions[0].key)
  const [domainId, setDomainId] = useState('')

  const selectedDuration = showDurationDropdown ? duration : timePeriod

  const domainOptions = [
    {
      key: '',
      value: '',
      label: intl.formatMessage({ id: 'cookieConsent.overview.domainFilter.allDomains' })
    },
    ...domains.map((domain) => ({
      key: domain?.id || '',
      value: domain?.id || '',
      label: domain.domain
    }))
  ]

  useEffect(() => {
    fetchCookieConsentsByRegion({ domainId, duration: dateMaps[selectedDuration] })
  }, [domainId, selectedDuration, fetchCookieConsentsByRegion])

  useEffect(() => {
    fetchCookieConsentDomains({
      sort_by: CookieConsentSortBy.updatedAt,
      sort_order: CookieConsentSortOrder.desc,
      include_cookies: true,
      include_jobs: true,
      include_region_banner_info: true,
      attributes: ['id', 'domain', 'websiteIntegratedScriptVersion', 'websiteIntegrationMode'],
      duration: dateMaps[selectedDuration]
    })
  }, [fetchCookieConsentDomains, selectedDuration])

  useEffect(() => {
    fetchCookieConsentVersions({})
  }, [fetchCookieConsentVersions])

  useEffect(() => {
    fetchCookieConsentByCategory({ duration: dateMaps[selectedDuration] })
  }, [fetchCookieConsentByCategory, selectedDuration])

  const consentsByRegionChartData = useMemo(() => {
    if (consentsByRegion) {
      return Object.keys(consentsByRegion)
        .map((region) => {
          return {
            name: region,
            additionalData: {
              [region]: {
                name: region,
                value: consentsByRegion[region].consentCount,
                domains: consentsByRegion[region].consentsByDomain
              }
            },
            [region]: consentsByRegion[region].consentCount,
            value: consentsByRegion[region].consentCount
          }
        })
        .sort((a, b) => b.value - a.value)
        .slice(0, 5)
    }
    return []
  }, [consentsByRegion])

  const countsFormatter = (value) => intl.formatNumber(value, { notation: 'compact' })

  const versionsSorted = Object.entries(versionsList || {}).sort(
    ([a], [b]) => Number(getCCVersionFromName(b)) - Number(getCCVersionFromName(a))
  )

  const cookieDomainCounters = useMemo(() => {
    let newScriptsCount = 0
    let totalCookies = 0
    let totalNewCookies = 0

    domains.forEach((domain) => {
      const { id = '', websiteIntegratedScriptVersion, cookieCount = 0, jobs = [] } = domain
      const latestVersion = versionsSorted.find(([_, domains]) => id in domains)?.[0] || ''
      const latestVerNum = latestVersion?.replace('cookie_consent_', '')
      if (websiteIntegratedScriptVersion && websiteIntegratedScriptVersion !== latestVerNum)
        newScriptsCount++

      const newCookiesCount = getNewCookiesCount(jobs, cookieCount)

      totalCookies += Number(cookieCount)
      if (newCookiesCount > 0) totalNewCookies += newCookiesCount
    })

    return { newScriptsCount, totalCookies, totalNewCookies }
  }, [domains, versionsSorted])

  const optInRate = useMemo(() => {
    let optInCount = 0
    let totalCount = 0

    Object.keys(consentsByCategory).forEach((category) => {
      optInCount += consentsByCategory[category].optInCount || 0
      totalCount +=
        (consentsByCategory[category].optInCount || 0) +
        (consentsByCategory[category].optOutCount || 0)
    })

    return optInCount !== 0 && totalCount !== 0 ? ((optInCount / totalCount) * 100).toFixed(2) : 0
  }, [consentsByCategory])

  const formatXAxis = (value) => {
    const hasMoreData = consentsByRegionChartData.length > 3
    return value.length > (hasMoreData ? 10 : 15)
      ? `${value.slice(0, hasMoreData ? 10 : 15)}...`
      : value
  }

  return (
    <WidgetCard
      widgetTitle={{
        title: widgetTitle || intl.formatMessage({ id: 'cookieConsent.overview.consentsByRegion' }),
        bordered: true,
        truncateTitle: true,
        dropdowns: [
          ...(showDurationDropdown
            ? [
                {
                  options: durationOptions,
                  selected: duration,
                  onChange: (_, data) => setDuration(data.value)
                }
              ]
            : []),
          {
            options: domainOptions,
            selected: domainId,
            popupClassName: 'cc-widget-custom-dropdown',
            onChange: (_, data) => setDomainId(data.value)
          }
        ]
      }}
      className="cookie-consent-widget-card responsive"
    >
      <LoaderView showLoader={showLoader} size="small" />
      <div className="align-start">
        {showSummary ? (
          <div className="xs-mr-32">
            <Link
              to={`${URL_COOKIES_CONSENT}${URL_DOMAINS}`}
              className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
            >
              <Typography variant={'basePlus6'} type={'strong'}>
                {domains?.length}
              </Typography>
              <Typography variant={'baseMinus2'} type={'medium'}>
                {intl.formatMessage({ id: 'privacyOpsWidget.totalDomains' })}
              </Typography>
            </Link>
            <Link
              to={`${URL_COOKIES_CONSENT}${URL_DOMAINS}`}
              className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
            >
              <Typography variant={'basePlus6'} type={'strong'}>
                {cookieDomainCounters.newScriptsCount}
              </Typography>
              <Typography variant={'baseMinus2'} type={'medium'}>
                {intl.formatMessage({ id: 'privacyOpsWidget.newScripts' })}
              </Typography>
            </Link>
            <Link
              to={`${URL_COOKIES_CONSENT}${URL_DOMAINS}`}
              className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
            >
              <Typography variant={'basePlus6'} className="align-center">
                <b>{cookieDomainCounters?.totalCookies || 0}</b>
                <Tags color="lavender" size={22} className="xs-ml-8" type="subtle">
                  {cookieDomainCounters?.totalNewCookies || 0}{' '}
                  {intl.formatMessage({ id: 'tables.table.changeType.new' })}
                </Tags>
              </Typography>
              <Typography variant={'baseMinus2'} type={'medium'}>
                {intl.formatMessage({ id: 'privacyOpsWidget.totalCookies' })}
              </Typography>
            </Link>
            <div className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4">
              <Typography variant={'basePlus6'} type={'strong'}>
                {optInRate || 0} %
              </Typography>
              <Typography variant={'baseMinus2'} type={'medium'}>
                {intl.formatMessage({ id: 'privacyOpsWidget.optInRate' })}
              </Typography>
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className="full-flex">
          {consentsByRegionChartData.length ? (
            <ChartBarSimple
              barColor={ChartColors.Green}
              barGap={0}
              minPointSize={0}
              data={consentsByRegionChartData}
              xLabelAngle={0}
              xAxisTickInterval={0}
              barCategoryGap={0}
              chartResponsiveHeight={200}
              xAxisLabelPadding={{ dx: 0, dy: 20 }}
              chartMargin={{ top: 5, right: 30, left: 20, bottom: 20 }}
              xAxisAngle={0}
              xAxisLine={true}
              cartesianGridHorrizontal
              tickFormatter={{
                fontSize: '0.72rem',
                fill: ChartColors.SecondaryText,
                textAnchor: 'middle',
                dx: -5
              }}
              customToolTip={<CustomTooltipCR />}
              yLabel={intl.formatMessage({
                id: 'cookieConsent.overview.charts.totalConsentLogs'
              })}
              yAxisLabelPadding={{ dx: -12, dy: -6 }}
              YTickFormatter={countsFormatter}
              XTickFormatter={formatXAxis}
            />
          ) : (
            <EmptyConsentsByRegionWidget />
          )}
          <ChartBarLegends
            linkTitle={intl.formatMessage({
              id: 'cookieConsent.overview.charts.viewAllDomains'
            })}
            linkHandler={() => historyService.push(URL_COOKIES_CONSENT + URL_DOMAINS)}
          />
        </div>
      </div>
    </WidgetCard>
  )
}

const mapStateToProps = (state: RootState) => ({
  domains: state.domains.domains.list,
  consentsByRegion: state.cookieConsent.overview.consentsByRegion,
  consentsByCategory: state.cookieConsent.overview.consentsByCategory,
  timePeriod: state.customDashboards.filterProps?.timePeriod,
  showLoader: getShowLoader(ACTION_GET_COOKIE_CONSENTS_BY_REGION)
})

const mapDispatchToProps = {
  fetchCookieConsentsByRegion,
  fetchCookieConsentDomains,
  fetchCookieConsentVersions,
  fetchCookieConsentByCategory
}

export default connect(mapStateToProps, mapDispatchToProps)(ConsentsByRegionWidget)
